import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button, Modal, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { ProductTableLite } from '../ProductTableLite'
import Loader from 'src/utils/Loader'
import { useTableColumns } from '../useTableColumns'
import { useTableData } from '../useTableData'
import { useTablePersistence } from '../useTablePersistence'
import { exportToCSV } from '../exportCSV'
import {
  SortState,
  FilterState,
  PaginationState,
  SelectedColumn,
} from '../types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_STATE, getDefaultColumnsFromLegacyRules } from '../defaultsFromLegacy'
import { isLinkingHubStore, shouldShowLinkingColumns } from '../linkingColumnVisibility'
import { usePermission } from 'src/hooks/usePermission'
import { useUserHavingWriteAccess } from 'src/utils/useUserHavingWriteAccess'
import { loggedInAdminAccessToken } from 'src/utils/constants'
import { delistVariantsAsync, getAllPlatformTypesAsync, refreshPlatformDataAsync } from 'src/redux/actions/platformActions'
import { getAllProductsVarientsFromMainChannelAsync, linkProductVarientAsync, productVarientResyncAsync } from 'src/redux/actions/productActions'
import useAppToast from 'src/hooks/useAppToast'
import { getApiErrorMessage, isApiPayloadSuccess } from 'src/marketConnect/helpers'
import BulkProductLinking from 'src/app/pages/listing/components/BulkProductLinking'
import CSVUploadModal from 'src/app/pages/listing/components/CSVUploadModal'
import UploadCsvModal from 'src/app/pages/create-product/UploadCsvModal'
import 'src/app/pages/listing/components/listing-products/Products.css'
import {
  getListingProductBulkActionOptions,
  isListingProductBulkActionPlatform,
} from 'src/utils/listingProductBulkActions'
import { isListingProductUploadPlatform } from 'src/utils/listingProductUploadPlatforms'
import { isRowResyncEligible } from '../bundleStatusUtils'

export const ListingProductTableLitePage: FC = () => {
  const dispatch = useAppDispatch()
  const { platformId, id: channelId } = useParams<{ platformId: string; id: string }>()
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const productVarientsFromMainChannel = useAppSelector((state: any) => state.productVarientFromMain || {})
  const userId = fetchedUserAccountData?.id
  const { successToast, errorToast } = useAppToast()

  // Permissions
  const { isRead, isWrite, isDelete } = usePermission()
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess()

  // Platform data
  const [currentChannelData, setCurrentChannelData] = useState<any>(null)
  const [mainStore, setMainStore] = useState(false)
  const [accountingStore, setAccountingStore] = useState(false)
  const [shippingStore, setShippingStore] = useState(false)
  const [erpStore, setErpStore] = useState(false)
  const [omsStore, setOmsStore] = useState(false)
  const [tabsData, setTabsData] = useState<any[]>([])
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const [refreshPlatformId, setRefreshPlatformId] = useState<number | null>(null)
  const [showBulkProductLinkingModal, setShowBulkProductLinkingModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState<any>(null)
  const [linkingCompleted, setLinkingCompleted] = useState(false)
  const [clickedProductVariant, setClickedProductVariant] = useState<any | null>(null)
  const [matchedParentProduct, setMatchedParentProduct] = useState<any | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [paginationMainData, setPaginationMainData] = useState({ page: 1, limit: 10, search_string: '' })
  const [platformModalId, setPlatformModalId] = useState<string>('')
  const [platformData, setPlatformData] = useState<any>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedItemForActions, setSelectedItemForActions] = useState<Array<number | string>>([])
  const [selectedAction, setSelectedAction] = useState('')
  const [opnDelistModal, setOpnDelistModal] = useState(false)
  const [isDelistIng, setIsDelistIng] = useState(false)
  const [resyncingSelected, setResyncingSelected] = useState(false)
  const columnsFetchInFlightRef = useRef<Promise<void> | null>(null)
  const initializedCatalogRouteRef = useRef<string | null>(null)
  const hasInitialized = useRef(false)
  const isTiktokPlatform = ((currentChannelData?.self_data?.name || '') as string).toUpperCase() === 'TIKTOK'
  const isLightspeedPlatform =
    ((currentChannelData?.self_data?.name || currentChannelData?.self_data?.code || '') as string).toUpperCase() ===
    'LIGHTSPEED'
  const isFairePlatform = ((currentChannelData?.self_data?.name || '') as string).toUpperCase() === 'FAIRE'
  const isWalmartPlatform = ((currentChannelData?.self_data?.name || '') as string).toUpperCase() === 'WALMART'

  const isOmsPlatform = useMemo(() => {
    const selfData = currentChannelData?.self_data
    const matchedPlatform = (fetchedConnectedPlatformData || []).find(
      (item: any) => Number(item?.id) === Number(channelId)
    )
    const platformType = String(
      selfData?.type ||
      selfData?.platform_info?.type ||
      selfData?.platform_info?.nature ||
      matchedPlatform?.type ||
      matchedPlatform?.platform_info?.type ||
      matchedPlatform?.platform_info?.nature ||
      ''
    ).trim().toUpperCase()

    return Boolean(selfData?.isOmsStore || matchedPlatform?.isOmsStore || platformType === 'OMS')
  }, [channelId, currentChannelData?.self_data, fetchedConnectedPlatformData])

  const canShowUploadButton = useMemo(() => {
    const platformName = String(currentChannelData?.self_data?.name || '').trim().toUpperCase()
    const hasOmsTemplate = Array.isArray(currentChannelData?.sample_product_files)
      && currentChannelData.sample_product_files.length > 0

    return isOmsPlatform || hasOmsTemplate || isListingProductUploadPlatform(platformName)
  }, [currentChannelData?.sample_product_files, currentChannelData?.self_data?.name, isOmsPlatform])

  const getFallbackChannelType = useCallback(
    (channel: any) => {
      const matchedPlatform = (fetchedConnectedPlatformData || []).find(
        (item: any) => Number(item?.id) === Number(channel?.id)
      )

      if (matchedPlatform?.isMainStore) return 'primary'
      if (matchedPlatform?.isAccountingStore) return 'accounting'
      if (matchedPlatform?.isShippingStore) return 'shipping'
      if (matchedPlatform?.isErpStore) return 'erp'
      if (matchedPlatform?.isOmsStore) return 'oms'

      const platformType = String(matchedPlatform?.platform_info?.type || '').trim().toUpperCase()
      if (platformType === 'OMS') return 'oms'

      return ''
    },
    [fetchedConnectedPlatformData]
  )

  const canShowCreateAndActions = (fetchedConnectedPlatformData || []).find((obj: any) => obj.id === Number(channelId))?.isOmsStore ?? false;


  const normalizeChannelType = useCallback(
    (channel: any) => {
      const apiType = String(channel?.type || '').trim().toLowerCase()
      if (apiType) return apiType
      return getFallbackChannelType(channel)
    },
    [getFallbackChannelType]
  )

  const normalizeTabsData = useCallback(
    (channels: any[]) =>
      (Array.isArray(channels) ? channels : []).map((channel: any) => ({
        ...channel,
        type: normalizeChannelType(channel),
      })),
    [normalizeChannelType]
  )

  // Fetch platform data
  useEffect(() => {
    if (!channelId) return

    dispatch(getAllPlatformTypesAsync({ platform_id: channelId })).then((res: any) => {
      const { data, self_data } = res?.payload || {}
      const main = Boolean(self_data?.isMainStore)
      const accounting = Boolean(self_data?.isAccountingStore)
      const shipping = Boolean(self_data?.isShippingStore)
      const erp = Boolean(self_data?.isErpStore)
      const oms = Boolean(self_data?.isOmsStore)

      setMainStore(main)
      setAccountingStore(accounting)
      setShippingStore(shipping)
      setErpStore(erp)
      setOmsStore(oms)
      setCurrentChannelData(res?.payload)
      const normalizedTabs = normalizeTabsData(data)
      setTabsData(normalizedTabs)
      if (normalizedTabs.length > 0) {
        setActiveTab(normalizedTabs[0].id)
      }

    })
  }, [channelId, dispatch, normalizeTabsData])

  useEffect(() => {
    if (!tabsData.length || !fetchedConnectedPlatformData?.length) return

    const normalizedTabs = normalizeTabsData(tabsData)
    const hasChanges = normalizedTabs.some(
      (channel: any, index: number) => channel?.type !== tabsData[index]?.type
    )

    if (hasChanges) {
      setTabsData(normalizedTabs)
    }
  }, [tabsData, fetchedConnectedPlatformData, normalizeTabsData])

  useEffect(() => {
    if (!showModal || !activeTab) return

    dispatch(
      getAllProductsVarientsFromMainChannelAsync({
        id: String(activeTab),
        paginationdata: paginationMainData,
      }) as any
    )
  }, [showModal, activeTab, paginationMainData, dispatch])

  // Persistence hook
  const persistence = useTablePersistence({
    userId: userId || 'guest',
    platformId: platformId || '0',
    channelId: channelId || '0',
  })

  // Initialize state from persistence or defaults
  const initialState = persistence.getInitialState()

  // State management
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>(
    initialState.selectedColumns || []
  )
  const [routeInitialSelectedColumns, setRouteInitialSelectedColumns] = useState<SelectedColumn[]>(
    initialState.selectedColumns || []
  )
  const [sortState, setSortState] = useState<SortState | null>(
    initialState.sortState || null
  )
  const [filterState, setFilterState] = useState<FilterState>(
    initialState.filterState || { globalSearch: '', columnFilters: [] }
  )
  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 1,
    pageSize: initialState.pageSize || DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const routeStateKey = `${platformId || '0'}:${channelId || '0'}`

  const areSelectedColumnsEqual = useCallback(
    (a: SelectedColumn[], b: SelectedColumn[]) => {
      if (a === b) return true
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i += 1) {
        const left = a[i]
        const right = b[i]
        if (
          left.field !== right.field ||
          left.order !== right.order ||
          left.width !== right.width ||
          (left.pinned || null) !== (right.pinned || null)
        ) {
          return false
        }
      }
      return true
    },
    []
  )

  useEffect(() => {
    const nextInitial = persistence.getInitialState()
    const routeInitialColumns = nextInitial.selectedColumns || []
    setCurrentChannelData(null)
    setTabsData([])
    setMainStore(false)
    setAccountingStore(false)
    setShippingStore(false)
    setErpStore(false)
    setActiveTab(null)
    setRouteInitialSelectedColumns(routeInitialColumns)
    setSelectedColumns(routeInitialColumns)
    setSortState(nextInitial.sortState || DEFAULT_SORT_STATE)
    setFilterState(nextInitial.filterState || { globalSearch: '', columnFilters: [] })
    setPaginationState({
      page: 1,
      pageSize: nextInitial.pageSize || DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 0,
    })
    setSelectedItemForActions([])
    setSelectedAction('')
    columnsFetchInFlightRef.current = null
    initializedCatalogRouteRef.current = null
    hasInitialized.current = false
  }, [routeStateKey])

  // Mark as initialized after each route hydration
  useEffect(() => {
    persistence.markInitialized()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformId, channelId])

  // Shared column config rules
  const showLinkingColumns = shouldShowLinkingColumns({
    mainStore,
    accountingStore,
    shippingStore,
    erpStore,
    omsStore,
    tabsData,
  })
  const mandatoryColumns = useMemo(
    () => (showLinkingColumns ? ['product_name', 'linking_status', 'link_actions'] : ['product_name']),
    [showLinkingColumns]
  )
  const defaultColumnOptions = useMemo(
    () => ({
      mainStore,
      accountingStore,
      shippingStore,
      erpStore,
      omsStore,
      hasLinkingColumns: showLinkingColumns,
      isTiktokPlatform,
      isFairePlatform,
      isLightspeedPlatform,
      isWalmartPlatform,
      tabsData: tabsData || [],
    }),
    [
      mainStore,
      accountingStore,
      shippingStore,
      erpStore,
      omsStore,
      showLinkingColumns,
      isTiktokPlatform,
      isFairePlatform,
      isLightspeedPlatform,
      isWalmartPlatform,
      tabsData,
    ]
  )

  // Data hook
  const {
    data,
    total,
    totalPages,
    loading: dataLoading,
    error: dataError,
    responseColumns,
    refetch,
  } = useTableData({
    channelId: channelId || '0',
    page: paginationState.page,
    pageSize: paginationState.pageSize,
    globalSearch: filterState.globalSearch,
    columnFilters: filterState.columnFilters,
    sortState,
    selectedColumns,
  })

  // Columns hook
  const {
    catalogColumns,
    selectedColumns: managedColumns,
    updateSelectedColumns,
    reorderColumns,
    pinColumn,
    getColumnByField,
    loading: columnsLoading,
    error: columnsError,
    refetch: refetchColumns,
  } = useTableColumns({
    channelId: channelId || '0',
    initialSelectedColumns: routeInitialSelectedColumns,
    isPrimaryStore: mainStore,
    defaultColumnOptions,
    mandatoryFields: mandatoryColumns,
    bootstrapColumns: responseColumns,
    autoFetchCatalog: false,
  })

  const ensureFieldsVisible = useCallback(
    (columns: SelectedColumn[], fields: string[]) => {
      const normalizedFields = Array.from(new Set(fields.filter(Boolean)))
      if (!normalizedFields.length) return columns

      const existingFields = new Set(columns.map((col) => col.field))
      let maxOrder = columns.length > 0 ? Math.max(...columns.map((col) => col.order)) : -1
      const next = [...columns]

      normalizedFields.forEach((field) => {
        if (existingFields.has(field)) return
        maxOrder += 1
        const catalogColumn = catalogColumns.find((col) => col.field === field)
        next.push({
          field,
          order: maxOrder,
          width: catalogColumn?.width,
          pinned: null,
        })
      })

      return next.map((col, idx) => ({ ...col, order: idx }))
    },
    [catalogColumns]
  )

  const isTruthyFlag = useCallback((value: any) => {
    if (value === true) return true
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized === 'true' || normalized === 'yes' || normalized === '1'
    }
    return false
  }, [])

  const isDefaultVisibleColumn = useCallback((col: any) => {
    if (!col) return false
    const advance = col?.advance_option || {}
    return (
      col?.allowVisibility === 'ALWAYS_VISIBLE' ||
      col?.allow_visiblity === 'ALWAYS_VISIBLE' ||
      col?.allow_visibility === 'ALWAYS_VISIBLE' ||
      Boolean(col?.fixed) ||
      isTruthyFlag(col?.default_visible) ||
      isTruthyFlag(col?.is_default) ||
      isTruthyFlag(col?.default) ||
      isTruthyFlag(advance?.default_visible) ||
      isTruthyFlag(advance?.is_default) ||
      isTruthyFlag(advance?.default)
    )
  }, [isTruthyFlag])

  const defaultVisibleFields = useMemo(() => {
    const normalizeApiField = (field?: string) => {
      if (!field) return ''
      if (field === 'name') return 'product_name'
      if (field === 'link_action') return 'link_actions'
      return field
    }

    const normalized = (catalogColumns || [])
      .map((col: any) => {
        const field = normalizeApiField(col?.field || col?.id || '')
        if (!field) return null
        const visibility = col?.allowVisibility || col?.allow_visiblity || col?.allow_visibility || 'YES'
        if (visibility === 'NO') return null
        return { field, col }
      })
      .filter((item): item is { field: string; col: any } => Boolean(item?.field))

    const apiDefaultFields = normalized
      .filter((item) => isDefaultVisibleColumn(item.col))
      .map((item) => item.field)

    const legacyDefaultFields = getDefaultColumnsFromLegacyRules({
      ...defaultColumnOptions,
      mainStore,
      hasLinkingColumns: showLinkingColumns,
    })
      .map((c) => c.field)
      .filter((field) => normalized.some((item) => item.field === field))

    const apiDefaultRemainder = apiDefaultFields.filter((field) => !legacyDefaultFields.includes(field))
    const finalFields = (
      legacyDefaultFields.length > 0
        ? [...legacyDefaultFields, ...apiDefaultRemainder]
        : apiDefaultFields.length > 0
          ? apiDefaultFields
          : normalized.map((item) => item.field)
    )
      .filter((field, index, arr) => arr.indexOf(field) === index)

    return finalFields
  }, [catalogColumns, mainStore, isDefaultVisibleColumn, defaultColumnOptions, showLinkingColumns])

  useEffect(() => {
    if (!channelId) return
    if (!currentChannelData?.self_data) return
    const currentMetaId = String(
      currentChannelData?.self_data?.id ??
      currentChannelData?.self_data?.platform_id ??
      ''
    )
    if (!currentMetaId || currentMetaId !== String(channelId)) return
    const hasLinkingContext = showLinkingColumns
    const catalogRouteKey = `${routeStateKey}:${hasLinkingContext ? 'link' : 'nolink'}`
    if (initializedCatalogRouteRef.current === catalogRouteKey) return
    initializedCatalogRouteRef.current = catalogRouteKey
    Promise.resolve(refetchColumns({ syncSelection: true }) as any).catch(() => { })
  }, [routeStateKey, channelId, currentChannelData?.self_data, showLinkingColumns, refetchColumns])

  // Sync managed columns with local state on initial load only
  useEffect(() => {
    if (managedColumns.length > 0 && !hasInitialized.current) {
      setSelectedColumns((prev) =>
        areSelectedColumnsEqual(prev, managedColumns) ? prev : managedColumns
      )
      hasInitialized.current = true
    }
    // Also sync whenever managedColumns changes (from reordering/pinning)
    if (hasInitialized.current && managedColumns.length > 0) {
      setSelectedColumns((prev) =>
        areSelectedColumnsEqual(prev, managedColumns) ? prev : managedColumns
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedColumns, areSelectedColumnsEqual])

  useEffect(() => {
    if (!isLinkingHubStore({ mainStore, omsStore }) || !showLinkingColumns) return
    if (!catalogColumns.length) return

    const requiredFields = ['product_name', 'linking_status', 'link_actions']
    const nextColumns = ensureFieldsVisible(selectedColumns, requiredFields)

    if (areSelectedColumnsEqual(selectedColumns, nextColumns)) return

    setSelectedColumns(nextColumns)
    updateSelectedColumns(nextColumns)
  }, [
    mainStore,
    omsStore,
    showLinkingColumns,
    catalogColumns,
    selectedColumns,
    ensureFieldsVisible,
    areSelectedColumnsEqual,
    updateSelectedColumns,
  ])

  // Update pagination total when data changes
  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      total,
      totalPages,
    }))
  }, [total, totalPages])

  // Persist state changes
  useEffect(() => {
    if (persistence.isInitialized()) {
      persistence.savePersistedState({
        selectedColumns,
        sortState,
        filterState,
        pageSize: paginationState.pageSize,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sortState, filterState, paginationState.pageSize])

  // Handlers
  const handleColumnsChange = useCallback(
    (columns: SelectedColumn[]) => {
      setSelectedColumns((prev) => (areSelectedColumnsEqual(prev, columns) ? prev : columns))
      updateSelectedColumns(columns)
    },
    [updateSelectedColumns, areSelectedColumnsEqual]
  )

  const handleReorderColumns = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      reorderColumns(dragIndex, hoverIndex)
      // The hook will update selectedColumns, which will sync back via useEffect
    },
    [reorderColumns]
  )

  const handlePinColumn = useCallback(
    (field: string, position: 'left' | 'right' | null) => {
      pinColumn(field, position)
      // Update local state to reflect the change
      setSelectedColumns(prev =>
        prev.map(col =>
          col.field === field ? { ...col, pinned: position } : col
        )
      )
    },
    [pinColumn]
  )

  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortState({ field, direction })
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters)
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState((prev) => ({ ...prev, pageSize, page: 1 }))
  }, [])

  const handleExportCSV = useCallback(() => {
    exportToCSV(data, selectedColumns, getColumnByField, channelId || '0', tabsData)
  }, [data, selectedColumns, getColumnByField, channelId, tabsData])

  const handleOpenColumnsConfig = useCallback(async () => {
    // Always fetch a fresh column catalog exactly once per open action, before showing drawer.
    if (columnsFetchInFlightRef.current) {
      await columnsFetchInFlightRef.current
      return
    }
    const pending = Promise.resolve(refetchColumns({ syncSelection: false }) as any).then(() => { })
    columnsFetchInFlightRef.current = pending
    try {
      await pending
    } finally {
      columnsFetchInFlightRef.current = null
    }
  }, [refetchColumns])

  const handleOpenBulkProductLinkingModal = useCallback(() => {
    setShowBulkProductLinkingModal(true)
    if (channelId) {
      setPlatformModalId(channelId)
      setPlatformData(currentChannelData?.self_data ?? null)
    }
  }, [channelId, currentChannelData])

  const handleOpenUploadModal = useCallback(() => {
    setShowUploadModal(true)
    if (channelId) {
      setPlatformModalId(channelId)
      setPlatformData(currentChannelData?.self_data ?? null)
    }
  }, [channelId, currentChannelData])

  const handleCloseLinkModal = useCallback(() => {
    setShowModal(false)
    setMatchedParentProduct(null)
    setSearchTerm('')
    setPaginationMainData({ page: 1, limit: 10, search_string: '' })
  }, [])

  const handlePaginationMain = useCallback((pageNumber: number) => {
    setPaginationMainData((prev) => ({ ...prev, page: pageNumber }))
  }, [])

  const handleLinkProductVariant = useCallback(() => {
    if (!matchedParentProduct || !clickedProductVariant || !clickedProductVariant.id) return

    setLinkingCompleted(true)
    const selectedChannel: any = tabsData.find((item: any) => item.id === Number(activeTab))
    const channelType = String(selectedChannel?.type || '').toLowerCase()
    const selectedChannelKey: Record<string, string> = {
      primary: 'parent_variant_id',
      oms: 'oms_parent_variant_id',
      accounting: 'accounting_parent_variant_id',
      shipping: 'shipping_parent_variant_id',
      erp: 'erp_parent_variant_id',
      erp_secondary: 'erp_secondary_parent_variant_id',
    }

    const payload: any = {
      variant_id: clickedProductVariant?.id,
    }
    const key = selectedChannelKey[channelType]
    if (key && matchedParentProduct?.id) {
      payload[key] = matchedParentProduct.id
    }

    dispatch(linkProductVarientAsync(payload) as any)
      .then((response: any) => {
        const successMessage = response?.payload?.message || response?.payload?.data?.message
        const errorMessage =
          response?.payload?.data?.message ||
          response?.payload?.data?.error ||
          response?.payload?.message

        if (linkProductVarientAsync.fulfilled.match(response)) {
          successToast(successMessage || 'Product Listing Linked Successfully')
          handleCloseLinkModal()
          refetch()
          return
        }

        errorToast(errorMessage || 'Failed to link product')
      })
      .catch(() => {
        errorToast('Failed to link product')
      })
      .finally(() => {
        setLinkingCompleted(false)
      })
  }, [
    matchedParentProduct,
    clickedProductVariant,
    tabsData,
    activeTab,
    dispatch,
    successToast,
    errorToast,
    handleCloseLinkModal,
    refetch,
  ])

  const handleRefreshData = useCallback(() => {
    if (!refreshPlatformId) return
    setRefreshing(true)
    dispatch(refreshPlatformDataAsync({ platformId: refreshPlatformId }) as any)
      .then((response: any) => {
        successToast(response?.payload?.data?.message || 'Refresh started')
        refetch()
      })
      .catch(() => {
        errorToast('Failed to refresh data')
      })
      .finally(() => {
        setRefreshing(false)
        setShowRefreshModal(false)
        setRefreshPlatformId(null)
      })
  }, [refreshPlatformId, dispatch, successToast, errorToast, refetch])

  const handleResyncClick = useCallback((rowData: any) => {
    if (!rowData?.id) return
    dispatch(productVarientResyncAsync({ failed_variant_ids: [Number(rowData.id)] }) as any)
      .then(() => {
        successToast('Failed Product resyncing started, status changed to Pending')
        refetch()
      })
      .catch(() => {
        errorToast('Order resync failed. Please try again.')
      })
  }, [dispatch, successToast, errorToast, refetch])

  const platformName = (currentChannelData?.self_data?.name || '').toUpperCase()
  const bulkActionOptions = useMemo(
    () => getListingProductBulkActionOptions(platformName),
    [platformName]
  )
  const allowedBulkActions = useMemo(
    () => new Set(bulkActionOptions.map((option) => option.value)),
    [bulkActionOptions]
  )

  const enableBulkActions =
    isWrite &&
    !mainStore &&
    !erpStore &&
    isUserHavingWriteAccess() &&
    isListingProductBulkActionPlatform(platformName)

  const enableResyncSelection =
    isWrite &&
    !mainStore &&
    isUserHavingWriteAccess()

  const selectedFailedVariantIds = useMemo(() => {
    if (!enableResyncSelection) return []
    const selectedIds = new Set((selectedItemForActions || []).map((id) => String(id)))
    return (data || [])
      .filter((row: any) => selectedIds.has(String(row?.id)) && isRowResyncEligible(row))
      .map((row: any) => Number(row.id))
      .filter((id: number) => Number.isFinite(id))
  }, [data, enableResyncSelection, selectedItemForActions])

  const handleResyncSelected = useCallback(() => {
    if (!selectedFailedVariantIds.length) return
    setResyncingSelected(true)
    dispatch(productVarientResyncAsync({ failed_variant_ids: selectedFailedVariantIds }) as any)
      .then(() => {
        successToast('Failed Product resyncing started, status changed to Pending')
        setSelectedItemForActions([])
        refetch()
      })
      .catch(() => {
        errorToast('Product resync failed. Please try again.')
      })
      .finally(() => {
        setResyncingSelected(false)
      })
  }, [dispatch, errorToast, refetch, selectedFailedVariantIds, successToast])

  const handleBulkActionChange = useCallback((action: string) => {
    if (action && !allowedBulkActions.has(action)) return
    setSelectedAction(action)
    if (action === 'delist' || action === 'relist' || action === 'full_update') {
      setOpnDelistModal(true)
    } else {
      setOpnDelistModal(false)
    }
  }, [allowedBulkActions])

  const handleConfirmBulkAction = useCallback(() => {
    if (!channelId || !selectedAction || !allowedBulkActions.has(selectedAction)) return
    setIsDelistIng(true)
    dispatch(
      delistVariantsAsync({
        platform_id: Number(channelId),
        delist_variant_ids: selectedItemForActions.map((id) => Number(id)),
        type: selectedAction,
      }) as any
    )
      .then((response: any) => {
        const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
        const platform = capitalize(currentChannelData?.self_data?.name || 'platform')
        const bulkActionErrorFallback =
          'Failed to update product listing status. Please try again.'

        if (isApiPayloadSuccess(response)) {
          if (response?.payload?.message) {
            successToast(
              `Your request has been successfully submitted to ${platform}. Updates will reflect on ${platform}'s platform once their API processes the request, which can take 15-20 minutes or longer. If you don't see the status change by then, please contact support.`
            )
          }
        } else {
          errorToast(getApiErrorMessage(response, bulkActionErrorFallback))
        }
        setSelectedAction('')
        setOpnDelistModal(false)
        setSelectedItemForActions([])
        refetch()
      })
      .catch((error) => {
        errorToast(
          getApiErrorMessage(error, 'Failed to update product listing status. Please try again.')
        )
      })
      .finally(() => {
        setIsDelistIng(false)
      })
  }, [channelId, selectedAction, allowedBulkActions, selectedItemForActions, dispatch, currentChannelData, successToast, errorToast, refetch])

  const productRecordRange = (() => {
    const count = productVarientsFromMainChannel?.ProductVarientCountFromMainChannel || 0
    const page = paginationMainData.page
    const limit = paginationMainData.limit
    if (count === 0) return { startRecord: 0, endRecord: 0 }
    const startRecord = (page - 1) * limit + 1
    const endRecord = Math.min(page * limit, count)
    return { startRecord, endRecord }
  })()

  const selectedVariantImageUrl =
    clickedProductVariant?.platform_product?.image_set?.[0]?.image_url ||
    'https://appconnect-images.s3.us-east-2.amazonaws.com/No_Image_Available.jpg'
  const selectedVariantName = clickedProductVariant?.name || ''
  const selectedPlatformProductName = clickedProductVariant?.platform_product?.name || ''
  const selectedVariantSku = clickedProductVariant?.sku || ''

  const handleResetColumns = useCallback(() => {
    const normalizeApiField = (field?: string) => {
      if (!field) return ''
      if (field === 'name') return 'product_name'
      if (field === 'link_action') return 'link_actions'
      return field
    }

    const normalized = (catalogColumns || [])
      .map((col: any) => {
        const field = normalizeApiField(col?.field || col?.id || '')
        if (!field) return null
        const visibility =
          col?.allowVisibility ||
          col?.allow_visiblity ||
          col?.allow_visibility ||
          'YES'
        if (visibility === 'NO') return null
        return { field, col }
      })
      .filter((item): item is { field: string; col: any } => Boolean(item?.field))

    const apiDefaultFields = normalized
      .filter((item) => isDefaultVisibleColumn(item.col))
      .map((item) => item.field)

    const legacyDefaultFields = getDefaultColumnsFromLegacyRules({
      ...defaultColumnOptions,
      mainStore,
      hasLinkingColumns: showLinkingColumns,
    })
      .map((c) => c.field)
      .filter((field) => normalized.some((item) => item.field === field))

    const apiDefaultRemainder = apiDefaultFields.filter((field) => !legacyDefaultFields.includes(field))
    const fields = (
      legacyDefaultFields.length > 0
        ? [...legacyDefaultFields, ...apiDefaultRemainder]
        : apiDefaultFields.length > 0
          ? apiDefaultFields
          : normalized.map((item) => item.field)
    )
      .filter((field, index, arr) => arr.indexOf(field) === index)

    const defaultsFromApi = fields.map((field: string, index: number) => ({ field, order: index }))

    // Clear localStorage
    persistence.clearPersistedState()
    // Reset selection using API response order.
    const safeDefaults =
      defaultsFromApi.length > 0 ? defaultsFromApi : [{ field: 'product_name', order: 0 }]
    setSelectedColumns(safeDefaults)
    updateSelectedColumns(safeDefaults)
    // Reset sort and filters
    setSortState(DEFAULT_SORT_STATE)
    setFilterState({ globalSearch: '', columnFilters: [] })
    setPaginationState(prev => ({ ...prev, page: 1 }))
    refetch()
  }, [
    persistence,
    updateSelectedColumns,
    catalogColumns,
    refetch,
    mainStore,
    isDefaultVisibleColumn,
    defaultColumnOptions,
    showLinkingColumns,
  ])

  const loading = dataLoading

  return (
    <div className="container-fluid px-0 py-2">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center justify-content-between">
            <h1 className="text-dark fw-bold fs-5 my-0">
              {mainStore
                ? 'Source Store Product'
                : isOmsPlatform
                ? 'Master Products'
                : erpStore
                  ? 'ERP Store Product'
                  : accountingStore
                    ? 'Accounting Store Product'
                    : shippingStore
                      ? 'Shipping Store Product'
                      : omsStore
                        ? 'Master Products'
                        : 'Destination Store Product'}
              {(currentChannelData?.self_data?.display_name || currentChannelData?.self_data?.name) && (
                <span> - {((name) => {
                  if (!name) return '';
                  return name.toUpperCase() === 'COMMERCIUMOMS' ? 'Commercium OMS' : name;
                })(currentChannelData.self_data.display_name || currentChannelData.self_data.name)}</span>
              )}
            </h1>

            {/* Action buttons */}
            <div className="d-flex gap-2">
              {/* Bulk Product Linking button */}
              {isWrite && currentChannelData?.is_product_mapping_allowed &&
                isUserHavingWriteAccess() && (
                  <button
                    key="bulk-linking"
                    className="btn btn-primary btn-sm d-inline-flex align-items-center"
                    onClick={handleOpenBulkProductLinkingModal}
                    style={{ fontSize: '14px', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="bi bi-file-earmark-arrow-up fs-4 me-2"></i>
                    <span>Bulk Product Linking</span>
                  </button>
                )}

              {/* Upload Files button (platform-specific) */}
              {canShowUploadButton &&
                isWrite &&
                isUserHavingWriteAccess() && (
                  <button
                    key="upload-files"
                    className="btn btn-primary btn-sm d-inline-flex align-items-center"
                    onClick={handleOpenUploadModal}
                    style={{ fontSize: '14px', padding: '0.25rem 0.5rem' }}
                  >
                    <i className="bi bi-file-earmark-arrow-up fs-4 me-2"></i>
                    <span>{isOmsPlatform ? 'Bulk Upload' : 'Product & Warehouse Upload'}</span>
                  </button>
                )}

              {/* Settings button */}
              {isWrite && isUserHavingWriteAccess() && (
                <Link
                  to={`/channel-settings/${platformId}/${channelId}`}
                  className="btn btn-primary btn-sm d-inline-flex align-items-center"
                  style={{ fontSize: '14px', padding: '0.25rem 0.5rem' }}
                >
                  <FontAwesomeIcon icon={faCog} className="me-2" style={{ fontSize: '14px' }} />
                  <span>Settings</span>
                </Link>
              )}

              {/* {(isUserHavingWriteAccess() || Boolean(localStorage.getItem(loggedInAdminAccessToken))) && (
                <button
                  className="btn btn-primary btn-sm d-inline-flex align-items-center"
                  style={{ fontSize: '14px', padding: '0.25rem 0.5rem' }}
                  onClick={() => {
                    if (!channelId) return
                    setShowRefreshModal(true)
                    setRefreshPlatformId(Number(channelId))
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsRotate} className="me-2" style={{ fontSize: '14px' }} />
                  <span>Refresh Data</span>
                </button>
              )} */}

            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          {/* Error display */}
          {(columnsError || dataError) && (
            <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-3 fs-3"></i>
              <div>
                <strong>Error loading data:</strong>
                <div className="mt-1">{columnsError || dataError}</div>
              </div>
            </div>
          )}

          <ProductTableLite
            data={data}
            loading={loading}
            catalogColumns={catalogColumns}
            selectedColumns={selectedColumns}
            onColumnsChange={handleColumnsChange}
            onReorderColumns={handleReorderColumns}
            onPinColumn={handlePinColumn}
            getColumnByField={getColumnByField}
            sortState={sortState}
            onSortChange={handleSortChange}
            filterState={filterState}
            onFilterChange={handleFilterChange}
            paginationState={paginationState}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRefresh={refetch}
            onExportCSV={handleExportCSV}
            onResetColumns={handleResetColumns}
            onResyncClick={handleResyncClick}
            onResyncSelected={enableResyncSelection ? handleResyncSelected : undefined}
            resyncSelectedCount={selectedFailedVariantIds.length}
            resyncSelectedLoading={resyncingSelected}
            enableBulkActions={enableBulkActions}
            selectedAction={selectedAction}
            onSelectedActionChange={handleBulkActionChange}
            bulkActionOptions={bulkActionOptions}
            selectedCount={selectedItemForActions.length}
            selectionEnabled={enableBulkActions || enableResyncSelection}
            selectedRowIds={selectedItemForActions}
            onSelectedRowIdsChange={setSelectedItemForActions}
            isRowSelectable={enableBulkActions ? undefined : isRowResyncEligible}
            platformId={platformId}
            channelId={channelId}
            mainStore={mainStore}
            erpStore={erpStore}
            tabsData={tabsData}
            mandatoryColumns={mandatoryColumns}
            defaultVisibleFields={defaultVisibleFields}
            onOpenColumnsConfig={handleOpenColumnsConfig}
            onLinkClick={(rowData) => {
              setClickedProductVariant(rowData)
              setShowModal(true)
              setMatchedParentProduct(null)
              setSearchTerm('')
              setPaginationMainData({ page: 1, limit: 10, search_string: '' })
            }}
          />

          <Modal
            show={showModal}
            onHide={handleCloseLinkModal}
            size='lg'
            centered
            className='listing-products-modal'
            backdropClassName='listing-products-backdrop'
          >
            <Modal.Header closeButton>
              <Modal.Title>Find Matching Product</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ overflowY: 'auto', maxHeight: '70vh' }}>
              {tabsData?.length ? (
                <Tabs
                  activeKey={activeTab || undefined}
                  onSelect={(k) => {
                    setActiveTab(k)
                    setMatchedParentProduct(null)
                    setSearchTerm('')
                    setPaginationMainData({ page: 1, limit: 10, search_string: '' })
                  }}
                  className='mb-3 custom-tabs-product'
                >
                  {tabsData.map((tab: any) => (
                    <Tab
                      eventKey={tab.id}
                      title={
                        <div className='d-flex flex-column align-items-center'>
                          <span>{tab.display_name || tab.name}</span>
                          <span className='fs-6'>{`(${tab.type})`}</span>
                        </div>
                      }
                      key={tab.id}
                    >
                      <div className='d-flex justify-content-between align-items-center mb-3'>
                        <div className='text-muted'>{`Search for an existing product of ${tab.display_name || tab.name} channel to link`}</div>
                        <div style={{ position: 'relative', width: '40%' }}>
                          <input
                            type='text'
                            className='form-control pr-5 custom-input'
                            placeholder='Enter Product Name or Store SKU'
                            value={searchTerm}
                            onChange={(ev) => setSearchTerm(ev.target.value)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter') {
                                setPaginationMainData((prev) => ({ ...prev, search_string: searchTerm, page: 1 }))
                              }
                            }}
                            style={{ border: '2px solid lightgray' }}
                          />
                        </div>
                      </div>

                      <div
                        className='d-flex align-items-center mx-1 px-1 mb-3'
                        style={{ backgroundColor: 'aliceblue', border: '4px inset lightgray' }}
                      >
                        <span className='symbol-label m-3' style={{ backgroundImage: 'none' }}>
                          <img
                            src={selectedVariantImageUrl}
                            alt='Selected Product'
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        </span>
                        <div>
                          <h4 className='mb-0'>
                            {selectedPlatformProductName}
                            {selectedVariantName &&
                              selectedVariantName !== 'Default Title' &&
                              selectedVariantName !== selectedPlatformProductName
                              ? ` - ${selectedVariantName}`
                              : ''}
                          </h4>
                          <h5 className='text-secondary mb-0'>Variant Sku: {selectedVariantSku || '-'}</h5>
                        </div>
                      </div>

                      {productVarientsFromMainChannel.loadingProductVarientFromMainChannel ? (
                        <div className='d-flex justify-content-center align-items-center py-4'>
                          <Loader variant="section" />
                        </div>
                      ) : (productVarientsFromMainChannel.productVarientFromMainChannel || []).length === 0 ? (
                        <div className='text-center text-muted py-4'>No Items Found</div>
                      ) : (
                        (productVarientsFromMainChannel.productVarientFromMainChannel || []).map((product: any, index: number) => {
                          const imageUrl =
                            product?.platform_product?.image_set?.[0]?.image_url ||
                            'https://appconnect-images.s3.us-east-2.amazonaws.com/No_Image_Available.jpg'
                          const variantLabel = product?.name && product?.name !== 'Default Title' ? product?.name : ''
                          const isSelected = matchedParentProduct?.id === product?.id

                          return (
                            <div
                              key={index}
                              className={`product-item mb-3 p-2 ${isSelected ? 'bg-light' : ''}`}
                              style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                              onClick={() => setMatchedParentProduct(product)}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div className='d-flex align-items-center me-3'>
                                  <img
                                    src={imageUrl}
                                    alt='Product'
                                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, marginRight: 12 }}
                                  />
                                  <div>
                                    <div className='fw-bold'>
                                      {product?.platform_product?.name}
                                      {variantLabel ? ` - ${variantLabel}` : ''}
                                    </div>
                                    <div className='text-muted'>{product?.sku}</div>
                                  </div>
                                </div>
                                <Button
                                  size='sm'
                                  variant={isSelected ? 'success' : 'outline-success'}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setMatchedParentProduct(product)
                                  }}
                                >
                                  {isSelected ? 'Selected' : 'Select'}
                                </Button>
                              </div>
                            </div>
                          )
                        })
                      )}

                      <div className='d-flex justify-content-between align-items-center mt-3'>
                        <div>
                          <strong>
                            Showing {productRecordRange.startRecord} - {productRecordRange.endRecord} of{' '}
                            {productVarientsFromMainChannel?.ProductVarientCountFromMainChannel || 0} Records
                          </strong>
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                          <Button
                            size='sm'
                            variant='light'
                            disabled={productVarientsFromMainChannel?.previous == null}
                            onClick={() => handlePaginationMain(paginationMainData.page - 1)}
                          >
                            Prev
                          </Button>
                          <Button
                            size='sm'
                            variant='light'
                            disabled={productVarientsFromMainChannel?.next == null}
                            onClick={() => handlePaginationMain(paginationMainData.page + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </Tab>
                  ))}
                </Tabs>
              ) : (
                <div className='text-muted'>No connected channels available.</div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant='primary'
                disabled={!isWrite || !matchedParentProduct || !clickedProductVariant || !isUserHavingWriteAccess() || linkingCompleted}
                onClick={handleLinkProductVariant}
              >
                {matchedParentProduct ? 'Link Product' : 'Select a product to link'}
              </Button>
              <Button variant='secondary' onClick={handleCloseLinkModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {currentChannelData?.is_product_mapping_allowed && (
            <BulkProductLinking
              show={showBulkProductLinkingModal}
              handleClose={() => setShowBulkProductLinkingModal(false)}
              id={platformModalId}
              platformData={platformData}
              available_map={currentChannelData?.available_map}
            />
          )}

          {isOmsPlatform ? (
            <UploadCsvModal
              show={showUploadModal}
              handleClose={() => setShowUploadModal(false)}
              platformId={platformModalId || channelId}
              connectedPlatformTypes={currentChannelData}
            />
          ) : (
            <CSVUploadModal
              show={showUploadModal}
              handleClose={() => setShowUploadModal(false)}
              id={platformModalId}
              platformData={platformData}
            />
          )}

          <Modal
            show={opnDelistModal}
            onHide={() => {
              setOpnDelistModal(false)
              setSelectedAction('')
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedAction === 'full_update'
                  ? 'Fully Update All Products?'
                  : selectedAction === 'relist'
                    ? 'Relist Selected Products?'
                    : 'Delist Selected Products?'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className='text-muted'>
              {selectedAction === 'full_update'
                ? 'Are you sure you want to fully update all products?'
                : selectedAction === 'relist'
                  ? 'Are you sure you want to relist the selected products?'
                  : 'Are you sure you want to delist the selected products?'}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant='secondary'
                onClick={() => {
                  setOpnDelistModal(false)
                  setSelectedAction('')
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' disabled={isDelistIng || !selectedAction} onClick={handleConfirmBulkAction}>
                {isDelistIng
                  ? selectedAction === 'full_update'
                    ? 'Updating...'
                    : selectedAction === 'relist'
                      ? 'Relisting...'
                      : 'Delisting...'
                  : 'Confirm'}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showRefreshModal}
            onHide={() => setShowRefreshModal(false)}
            centered
            className='listing-products-modal'
            backdropClassName='listing-products-backdrop'
          >
            <Modal.Header closeButton>
              <Modal.Title>Refresh Data</Modal.Title>
            </Modal.Header>
            <Modal.Body className='text-muted'>
              This might take 10-15 minutes to complete. Are you sure you want to refresh product data for this channel?
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setShowRefreshModal(false)}>
                Cancel
              </Button>
              <Button variant='primary' onClick={handleRefreshData} disabled={refreshing}>
                {refreshing ? 'Refreshing...' : 'Confirm'}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  )
}
